import { RestaurantContext } from 'Contexts/RestaurantContext';
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useContext,
  useRef,
} from 'react';
import { EditReservation, FloorPlan } from 'types/reservations';
import { ReservationShift } from 'types/shifts';
import { dateHelper, timeHelper, timeToBlock } from 'utils/helper';
import { getRunningShiftId } from 'utils/reservationShift';
import server from 'utils/server';
import { shiftStartOfWeek } from './useCalendar';
import useImpulse from './useImpulse';
import useCustomEvent, { triggerCustomEvent } from 'CustomHooks/useCustomEvent';
import useDocument from './useDocument';

const useFloorPlan = (
  restaurantId: string | null,
  date: string,
  editReservation: EditReservation | null,
  floorPlans: FloorPlan[],
  isLightReservation: boolean = false,
  currentTime: string | null = null
) => {
  const { reservationSettings, experimentalFlags } =
    useContext(RestaurantContext);

  const sharedTablesEnabled = !!reservationSettings?.enableSharedTables;

  const [shifts, setShifts] = useState<null | ReservationShift[]>(null);

  // Holds the day currently on screen, so a reload can drop a response for a
  // day the user has already left.
  const shiftKeyRef = useRef('');
  // Two loads for the same day can still finish out of order, so only the
  // latest request may write.
  const shiftRequestRef = useRef(0);
  const visibleShiftRequestRef = useRef<number | null>(null);
  const pendingSilentKeyRef = useRef<string | null>(null);
  // The selection can change while a shift request is on its way, so a
  // response reads it from here instead of from its closure.
  const selectedShiftRef = useRef<string | null>(null);

  const finishShiftRequest = (requestId: number, requestKey: string) => {
    if (visibleShiftRequestRef.current !== requestId) return;
    visibleShiftRequestRef.current = null;
    const pendingKey = pendingSilentKeyRef.current;
    pendingSilentKeyRef.current = null;
    if (pendingKey === requestKey && shiftKeyRef.current === requestKey) {
      triggerCustomEvent('triggerShiftReload', { silent: true });
    }
  };

  const triggerShiftReload = useCustomEvent('triggerShiftReload', (e) => {
    let newDate = editReservation?.date || date;
    const silent = !!e.detail?.silent;
    const requestKey = `${restaurantId}|${newDate}`;
    if (silent && visibleShiftRequestRef.current !== null) {
      pendingSilentKeyRef.current = requestKey;
      return;
    }
    const requestId = ++shiftRequestRef.current;
    if (!silent) visibleShiftRequestRef.current = requestId;

    setcurrentDate(newDate);
    setcurrentRestaurant(restaurantId);

    console.log('triggerShiftReload');

    let oldShift = shifts?.find((s) => s.id === currentShift);

    // A silent reload keeps the tables on screen: `loading` hides them.
    if (!silent) setloading(true);
    server
      .get(`/v03/shifts/${restaurantId}/${newDate}`)
      .then((response) => {
        if (
          shiftKeyRef.current !== requestKey ||
          shiftRequestRef.current !== requestId
        )
          return;

        let shifts = response.data.shifts as ReservationShift[];

        const selected = selectedShiftRef.current;

        // The name fallback belongs to the selection this request started with.
        let cShift =
          selected === null
            ? undefined
            : shifts.find((s) => s.id === selected) ??
              (selected === currentShift
                ? shifts.find((s) => s.name === oldShift?.name)
                : undefined);

        if (!cShift && selected !== null) {
          setCurrentShift(null);
        }

        if (shifts.length === 0) {
          setfloorPlanId(null);
          setclosed({ message: 'Restaurant is closed', error: false });
        } else if (shifts.length === 1) {
          let shift = shifts[0];
          setfloorPlanId(shift.additional?.floorPlan || null);
          if (shift.closed) {
            setclosed({ message: shift?.name || null, error: false });
          } else {
            setclosed(false);
          }
        } else if (cShift) {
          setfloorPlanId(cShift.additional?.floorPlan || null);
          setclosed(false);
        } else {
          //Put back the standard floorPlan
          const shiftFloorPlans = shifts
            .map((s) => s.additional?.floorPlan || null)
            .filter((fP) => !!fP);
          if (!shiftFloorPlans.length) {
            setfloorPlanId(null);
          } else if (new Set(shiftFloorPlans).size === 1) {
            setfloorPlanId(shiftFloorPlans[0]);
          } else {
            //To handle if several floor plans are set up for the different shifts of that day
            setfloorPlanId(null);
          }
          setclosed(false);
        }

        setloading(false);

        setShifts(shifts);
      })
      .catch((error) => {
        console.error(error);

        // A failure for a day the user already left, or an outdated request,
        // must not touch the screen.
        if (
          shiftKeyRef.current !== requestKey ||
          shiftRequestRef.current !== requestId
        )
          return;

        // A failed background reload keeps the last successfully loaded plan.
        if (silent) return;

        if (error.response) {
          setclosed({
            message:
              error.response?.data?.message ||
              'Netzwerk Fehler (Internetverbindung)',
            error: false,
          });
        } else {
          setclosed({
            message: 'Netzwerk Fehler (Internetverbindung)',
            error: false,
          });
        }
        setCurrentShift(null);
        setloading(false);
        setShifts([]);
      })
      .finally(() => finishShiftRequest(requestId, requestKey));
  });

  const [closed, setclosed] = useState<
    false | { message: string | null; error: boolean }
  >(false);
  const [loading, setloading] = useState<boolean>(true);
  const [floorPlanId, setfloorPlanId] = useState<null | string>(null);

  const [currentDate, setcurrentDate] = useState<string | null>(null);
  const [currentRestaurant, setcurrentRestaurant] = useState<string | null>(
    null
  );

  const [currentShift, setCurrentShift] = useState<null | string>(null);

  selectedShiftRef.current = currentShift;

  const [space, setspace] = useState<string | null>('');

  useEffect(() => {
    let newDate = editReservation?.date || date;
    const requestKey = `${restaurantId}|${newDate}`;

    if (
      restaurantId &&
      (currentDate !== newDate || currentRestaurant !== restaurantId)
    ) {
      setcurrentDate(newDate);
      setcurrentRestaurant(restaurantId);
      const requestId = ++shiftRequestRef.current;
      visibleShiftRequestRef.current = requestId;

      setloading(true);
      server
        .get(`/v03/shifts/${restaurantId}/${newDate}`)
        .then((response) => {
          // A slower response for a day the user already left (e.g. today's
          // load right after login, before the linked date) must not replace
          // the shifts of the day on screen.
          if (
            shiftKeyRef.current !== requestKey ||
            shiftRequestRef.current !== requestId
          )
            return;

          let shifts = response.data.shifts as ReservationShift[];

          const selected = selectedShiftRef.current;

          let cShift = shifts.find((s) => s.id === selected);

          if (!cShift && selected !== null) {
            setCurrentShift(null);
          }

          if (shifts.length === 0) {
            setfloorPlanId(null);
            setclosed({ message: 'Restaurant is closed', error: false });
          } else if (shifts.length === 1) {
            let shift = shifts[0];
            setfloorPlanId(shift.additional?.floorPlan || null);
            if (shift.closed) {
              setclosed({ message: shift?.name || null, error: false });
            } else {
              setclosed(false);
            }
          } else if (cShift) {
            setfloorPlanId(cShift.additional?.floorPlan || null);
            setclosed(false);
          } else {
            //Put back the standard floorPlan
            const shiftFloorPlans = shifts
              .map((s) => s.additional?.floorPlan || null)
              .filter((fP) => !!fP);
            if (!shiftFloorPlans.length) {
              setfloorPlanId(null);
            } else if (new Set(shiftFloorPlans).size === 1) {
              setfloorPlanId(shiftFloorPlans[0]);
            } else {
              //To handle if several floor plans are set up for the different shifts of that day
              setfloorPlanId(null);
            }
            setclosed(false);
          }

          setloading(false);

          setShifts(shifts);
        })
        .catch((error) => {
          console.error(error);

          if (
            shiftKeyRef.current !== requestKey ||
            shiftRequestRef.current !== requestId
          )
            return;

          if (error.response) {
            setclosed({
              message:
                error.response?.data?.message ||
                'Netzwerk Fehler (Internetverbindung)',
              error: false,
            });
          } else {
            setclosed({
              message: 'Netzwerk Fehler (Internetverbindung)',
              error: false,
            });
          }
          setCurrentShift(null);
          setloading(false);
          setShifts([]);
        })
        .finally(() => finishShiftRequest(requestId, requestKey));
    }
  }, [date, editReservation, restaurantId, triggerShiftReload]);

  const shiftDate = editReservation?.date || date;

  shiftKeyRef.current = `${restaurantId}|${shiftDate}`;

  // Shifts are only fetched on date/restaurant change, so a space or table
  // blocked on another device stayed invisible until a reload. The day document
  // is also rewritten per reservation (shiftStatistics), hence the reload only
  // fires when the shifts themselves changed.
  const [dayDoc] = useDocument<{ date?: string; shifts?: ReservationShift[] }>(
    restaurantId ? `restaurants/${restaurantId}/reservationDates` : '',
    restaurantId ? shiftDate : null
  );

  const lastShiftSnapshot = useRef<{ key: string; value: string } | null>(null);

  useEffect(() => {
    // useDocument still holds the previous document right after the date
    // changed, so only take a snapshot that belongs to the day on screen.
    if (!restaurantId || !dayDoc.data?.shifts) return;
    if (dayDoc.data.date !== shiftDate) return;

    const key = shiftKeyRef.current;
    const value = JSON.stringify(dayDoc.data.shifts);

    if (lastShiftSnapshot.current?.key !== key) {
      lastShiftSnapshot.current = { key, value };
      return;
    }

    if (lastShiftSnapshot.current.value === value) return;

    lastShiftSnapshot.current = { key, value };
    triggerCustomEvent('triggerShiftReload', { silent: true });
  }, [dayDoc.data, restaurantId, shiftDate]);

  const onSpaceChange = (id: string) => setspace(id);

  const runningShift = useMemo(
    () =>
      experimentalFlags?.data?.onlyShowReservationsThatStartInCurrentShift &&
      currentTime &&
      shifts &&
      shiftDate === dateHelper()
        ? getRunningShiftId(shifts, timeToBlock(currentTime))
        : null,
    [experimentalFlags?.data, currentTime, shifts, shiftDate]
  );

  const lastRunningShift = useRef<string | null>(null);

  // The book follows the clock. A shift picked by hand stays, "all day" only
  // lasts until the next shift starts. Layout effect, so a shift start does
  // not paint the old selection first.
  useLayoutEffect(() => {
    const last = lastRunningShift.current;
    lastRunningShift.current = runningShift;

    setCurrentShift((selected) =>
      selected === null || selected === last ? runningShift : selected
    );
  }, [runningShift]);

  useEffect(() => {
    if (currentShift && !!shifts?.length) {
      let current = shifts.find((s) => s.id === currentShift);

      if (current && !isLightReservation) {
        setfloorPlanId(current.additional?.floorPlan || null);
      }
    }
  }, [currentShift, shifts]);

  const rawFloorPlan = useMemo(() => {
    if (!floorPlans.length || isLightReservation) {
      return null;
    } else if (floorPlanId !== null) {
      let floorPlan = floorPlans.find((f) => f.id === floorPlanId);

      if (floorPlan) {
        return floorPlan;
      }
    }

    if (currentDate === dateHelper()) {
      let currentTime = timeToBlock(timeHelper());

      let shift = shifts?.find(
        (s) => s.start <= currentTime && s.close >= currentTime && !s.closed
      );

      if (shift?.additional?.floorPlan) {
        let floorPlan = floorPlans.find(
          (f) => f.id === shift?.additional?.floorPlan
        );

        if (floorPlan) {
          return floorPlan;
        }
      }
    }

    return floorPlans.find((f) => f.standard) || null;
  }, [floorPlans, floorPlanId, isLightReservation]);

  // Feature-flag gate, same as the API does in HelperClasses.Table: shared
  // seating only applies when the restaurant opted in via
  // settings.reservationsV02.enableSharedTables. The floor plan editor keeps
  // showing the real per-table flag, only the reservation book is gated.
  const floorPlan = useMemo(() => {
    if (!rawFloorPlan || sharedTablesEnabled) return rawFloorPlan;

    return {
      ...rawFloorPlan,
      tables: rawFloorPlan.tables.map((t) => ({ ...t, shared: false })),
      tableCombinations: rawFloorPlan.tableCombinations?.map((c) => ({
        ...c,
        shared: false,
      })),
    };
  }, [rawFloorPlan, sharedTablesEnabled]);

  useEffect(() => {
    if (floorPlan) {
      if (!space || !floorPlan.spaces.find((s) => s.id === space)) {
        setspace(floorPlan.spaces?.[0]?.id || null);
      }
    } else {
      setspace(null);
    }
  }, [floorPlan]);

  // console.log({
  //     space,
  //     floorPlan,
  //     shifts,
  //     closed,
  //     loading,
  //     currentShift,
  // });

  const impulse = useImpulse(900000);

  const endOfShift = useMemo(() => {
    let today = dateHelper();

    if (date < today) return true;

    if (!shifts?.length || date > today) return false;

    const lastShift = shifts
      .filter((s) => !s.closed && !!s.close)
      .sort((a, b) => b.close - a.close)?.[0];

    return lastShift?.close <= timeToBlock(timeHelper()) + 1;
  }, [shifts, impulse, date]);

  return {
    space,
    endOfShift,
    onSpaceChange,
    floorPlan,
    shifts,
    closed,
    loading,
    currentShift,
    setCurrentShift,
    setShifts,
  };
};

export default useFloorPlan;
